import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
  Tick
} from 'chart.js';
import { AnyElement } from 'canvg';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function SubscriptionReasonSatisfaction() {
  // Dynamic data structure for easy JSON implementation
  const pageData = {
    title: "Subscription Awareness & Usage",
    pageNumber: 30,
    
    // Reason for Trying Section
    reasonSection: {
      title: "Reason for Trying Subscription",
      chartData: [
        { label: "Convenience of delivery", value: 51.2 },
        { label: "Potential cost savings", value: 57.6 },
        { label: "Access to premium brands", value: 23.0 },
        { label: "Positive reviews/ friend/vet/customer", value: 14.0 },
        { label: "Customization/ more grain", value: 10.5 },
        { label: "Influencer/celebrity endorsement", value: 3.4 }
      ]
    },
    
    // Satisfaction Section
    satisfactionSection: {
      title: "Subscription Satisfaction",
      chartData: [
        { label: "Top 2 Box Satisfaction", value: 75.3 },
        { label: "Middle = Box", value: 21.4 },
        { label: "Bottom 2 Box", value: 3.3 }
      ]
    },
    
    // Insight Boxes
    insights: [
      {
        id: 1,
        text: "Convenience is the dominant value proposition",
        details: "Nearly half, subscribe for a convenient automated solution. Predictability and time-saving are key drivers, appealing to busy households."
      },
      {
        id: 2,
        text: "Cost savings remain a powerful motivator",
        details: [
          "57% cite cost savings as a reason for subscribing.",
          "Those motivated RANK this as their primary (18%)",
          "Bundling, exclusive offers drive consideration."
        ]
      },
      {
        id: 3,
        text: "Premium and 'health-oriented' brands motivate a niche but valuable segment",
        details: "23% subscribe to access specialty formulas. This reflects ongoing shift toward health-conscious product preferences, with strong appeal for 'better-lifestyle' positioning."
      },
      {
        id: 4,
        text: "Social proof plays a moderate role",
        details: [
          "Only 14% rely on customer/friend reviews",
          "Influencers matter for just 7%, typically among pet-focused social media followers or younger audiences."
        ]
      },
      {
        id: 5,
        text: "Satisfaction is high, reflecting recurring usage",
        details: "With 75% Top 2 Box satisfaction, the subscription model delivers on core expectations - convenience, consistency, and value"
      },
      {
        id: 6,
        text: "Dissatisfaction is rare and mostly tied to cost or meal fit",
        details: "Bottom 2 Box (3%), reflecting complaints over price or portion mismatch."
      }
    ]
  };

  // Chart configurations
  const reasonChartData = {
    labels: pageData.reasonSection.chartData.map(item => item.label),
    datasets: [{
      data: pageData.reasonSection.chartData.map(item => item.value),
      backgroundColor: '#aee772',
      barThickness: 25,
    }]
  };

  const satisfactionChartData = {
    labels: pageData.satisfactionSection.chartData.map(item => item.label),
    datasets: [{
      data: pageData.satisfactionSection.chartData.map(item => item.value),
      backgroundColor: '#aee772',
      barThickness: 45,
    }]
  };

  const chartOptions:ChartOptions<"bar"> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
            label: function(tooltipItem: TooltipItem<'bar'>) {
                return `Value: ${tooltipItem.raw}`;
            }
            }
      }
    },
    scales: {
       x: {
      beginAtZero: true,
      max: 70,
      ticks: {
        stepSize: 10,
        font: { size: 10 },
        callback: function (tickValue: string | number, index: number, ticks: Tick[]) {
          return `${tickValue}%`;
        },
      },
      grid: {
        display: true,
        color: '#e0e0e0',
      },
    },
      y: {
        ticks: {
          font: { size: 10 },
          crossAlign: 'far',
          padding: 5
        },
        grid: { display: false }
      }
    },
    layout: {
      padding: { right: 10 }
    }
  };

    const satisfactionOptions = {
    ...chartOptions,
    scales: {
        ...chartOptions.scales,   // safe if defined
        x: {
        ...(chartOptions.scales?.x || {}), // fallback to empty object
        max: 80
        }
    }
    };

  return (
     <div className="flex flex-col h-full items-center w-full">
      <h1 className="relative inline-block text-white text-center font-extrabold 
  max-w-4xl text-2xl lg:text-4xl font-nunito mb-4 
  bg-gradient-to-r from-[#92D050] to-[#8CC54F] 
  px-6 py-2
  before:content-[''] before:absolute before:left-0 before:top-1/2 
  before:-translate-y-1/2 before:-translate-x-full
  before:border-y-[16px] before:border-r-[16px] before:border-y-transparent 
  before:border-r-[#92D050]

  after:content-[''] after:absolute after:right-0 after:top-1/2 
  after:-translate-y-1/2 after:translate-x-full
  after:border-y-[16px] after:border-l-[16px] after:border-y-transparent 
  after:border-l-[#8CC54F]">{pageData.title}</h1>
   

      {/* Main Grid Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Charts */}
        <div className="space-y-6">
          {/* Reason for Trying Chart */}
          <div>
            <h2 className="text-base font-bold mb-3">{pageData.reasonSection.title}</h2>
             <div className='h-[200px]' >
              <Bar data={reasonChartData} options={chartOptions} />
            </div>
          </div>

          {/* Satisfaction Chart */}
          <div>
            <h2 className="text-base font-bold mb-3">{pageData.satisfactionSection.title}</h2>
            <div className='h-[200px]' >
              <Bar data={satisfactionChartData} options={satisfactionOptions} />
            </div>
          </div>
        </div>

        {/* Right Column - Insights */}
        <div className="space-y-1">
          {pageData.insights.map((insight) => (
            <div 
              key={insight.id}
              className="border-2 border-green-700 bg-[#aee772] rounded-xl p-2 shadow-sm text-sm leading-relaxed"
            >
              <p className="text-sm font-bold mb-1">{insight.text}</p>
              {Array.isArray(insight.details) ? (
                <ul className="space-y-1">
                  {insight.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start text-xs">
                      <span className="mr-2">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs leading-relaxed">{insight.details}</p>
              )}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}